const STRAPI_BASE_URL = process.env.REACT_APP_STRAPI_BASE_URL
  ? process.env.REACT_APP_STRAPI_BASE_URL.replace(/\/$/, '')
  : '';
const DEFAULT_STRAPI_PAGE_SIZE = 100;

const buildStrapiUrl = (path, params = {}) => {
  if (!STRAPI_BASE_URL) return null;
  const url = new URL(`${STRAPI_BASE_URL}/api${path}`);
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, value);
  });

  const queryString = searchParams.toString();
  if (queryString) url.search = queryString;
  return url.toString();
};

const fetchStrapi = async (path, params) => {
  const url = buildStrapiUrl(path, params);
  if (!url) return null;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Strapi request failed: ${response.status}`);
  }
  return response.json();
};

const normalizeEntity = entity => {
  if (!entity) return null;
  if (entity.attributes) return { id: entity.id, ...entity.attributes };
  return entity;
};

const normalizeCollection = response => {
  const data = response?.data;
  if (!data) return [];
  if (Array.isArray(data)) return data.map(normalizeEntity);
  return [normalizeEntity(data)];
};

const normalizeMedia = media => {
  const data = media?.data ?? media;
  if (!data) return null;
  if (data.attributes) return { id: data.id, ...data.attributes };
  return data;
};

const normalizeRelation = relation => {
  const data = relation?.data ?? relation;
  if (!data) return null;
  if (Array.isArray(data)) return data.map(normalizeEntity).filter(Boolean);
  return normalizeEntity(data);
};

const normalizeMediaCollection = media => {
  const data = media?.data ?? media;
  if (!data) return [];
  const arr = Array.isArray(data) ? data : [data];
  return arr.map(normalizeMedia).filter(Boolean);
};

const normalizeCommunityCategory = category => {
  if (!category) return null;

  return {
    ...category,
    heroImage: normalizeMedia(category.heroImage),
  };
};

const normalizeText = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase();

export const normalizeCommunityCategorySlug = value => normalizeText(value);

const filterByCommunityCategorySlug = (entities, categorySlug) => {
  const normalizedCategorySlug = normalizeCommunityCategorySlug(categorySlug);
  if (!normalizedCategorySlug) return entities;

  return entities.filter(
    entity => normalizeText(entity?.category?.slug) === normalizedCategorySlug
  );
};

const getEntityFeaturedValue = entity =>
  Boolean(entity?.featuredOnHomepage ?? entity?.featured ?? entity?.isFeatured);

const getEntitySortOrderValue = entity => {
  const rawValue = entity?.sortOrder;
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return Number.MAX_SAFE_INTEGER;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const getEntityDisplayLabel = entity =>
  normalizeText(
    entity?.name ||
      entity?.title ||
      entity?.slug ||
      entity?.state?.name ||
      entity?.stateName ||
      entity?.locationState
  );

const sortByFeaturedAndOrder = (a, b) => {
  const featuredA = getEntityFeaturedValue(a);
  const featuredB = getEntityFeaturedValue(b);
  if (featuredA !== featuredB) {
    return featuredA ? -1 : 1;
  }

  const sortOrderA = getEntitySortOrderValue(a);
  const sortOrderB = getEntitySortOrderValue(b);
  if (sortOrderA !== sortOrderB) {
    return sortOrderA - sortOrderB;
  }

  const labelA = getEntityDisplayLabel(a);
  const labelB = getEntityDisplayLabel(b);
  return labelA.localeCompare(labelB);
};

const fetchAllStrapiCollection = async (path, params = {}) => {
  let page = 1;
  let pageCount = 1;
  const allItems = [];

  do {
    const response = await fetchStrapi(path, {
      ...params,
      'pagination[page]': page,
      'pagination[pageSize]': params['pagination[pageSize]'] || DEFAULT_STRAPI_PAGE_SIZE,
    });

    allItems.push(...normalizeCollection(response));
    pageCount = response?.meta?.pagination?.pageCount || page;
    page += 1;
  } while (page <= pageCount);

  return allItems;
};

const normalizeInstructor = instructor => {
  if (!instructor) return null;
  const normalizedState = normalizeRelation(instructor.state);
  const state = normalizedState && !Array.isArray(normalizedState) ? normalizedState : null;
  const normalizedCategory = normalizeRelation(instructor.category);
  const category =
    normalizedCategory && !Array.isArray(normalizedCategory)
      ? normalizeCommunityCategory(normalizedCategory)
      : null;

  return {
    ...instructor,
    state,
    category,
    stateName: state?.name || instructor?.stateName || instructor?.locationState || '',
    websiteUrl: normalizeExternalUrl(instructor.websiteUrl),
    birdBamUrl: normalizeExternalUrl(instructor.birdBamUrl),
    instagramUrl: normalizeExternalUrl(instructor.instagramUrl),
    facebookUrl: normalizeExternalUrl(instructor.facebookUrl),
    profileImage: normalizeMedia(instructor.profileImage),
    coverImage: normalizeMedia(instructor.coverImage),
  };
};

const normalizeInstructorState = state => {
  if (!state) return null;
  const instructors = normalizeRelation(state.instructors);
  const normalizedInstructors = Array.isArray(instructors)
    ? instructors.map(normalizeInstructor).filter(Boolean)
    : [];

  return {
    ...state,
    heroImage: normalizeMedia(state.heroImage),
    instructors: normalizedInstructors,
    instructorCount: normalizedInstructors.length,
  };
};

const normalizeEvent = event => {
  if (!event) return null;

  const normalizedState = normalizeRelation(event.state);
  const state = normalizedState && !Array.isArray(normalizedState) ? normalizedState : null;
  const normalizedCategory = normalizeRelation(event.category);
  const category =
    normalizedCategory && !Array.isArray(normalizedCategory)
      ? normalizeCommunityCategory(normalizedCategory)
      : null;
  const stateName = state?.name || event.locationState || '';
  const city = event.city || event.locationCity || '';
  const locationText =
    event.locationText || [city, stateName].filter(Boolean).join(', ') || 'Location TBA';

  return {
    ...event,
    coverImage: normalizeMedia(event.coverImage),
    gallery: normalizeMediaCollection(event.gallery),
    state,
    category,
    stateName,
    locationCity: city,
    locationState: stateName,
    locationText,
    isFeatured: Boolean(event.featuredOnHomepage ?? event.isFeatured),
    websiteUrl: normalizeExternalUrl(event.websiteUrl),
    registrationUrl: normalizeExternalUrl(event.registrationUrl),
  };
};

const ensureHttpsProtocol = value => {
  if (!value) return '';
  const trimmed = `${value}`.trim();
  if (!trimmed) return '';
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const normalizeExternalUrl = value => {
  const withProtocol = ensureHttpsProtocol(value);
  if (!withProtocol) return '';

  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return '';
    }
    return parsed.toString();
  } catch (error) {
    return '';
  }
};

export const getStrapiMediaUrl = media => {
  const normalized = normalizeMedia(media);
  const url = normalized?.url;
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return STRAPI_BASE_URL ? `${STRAPI_BASE_URL}${url}` : url;
};

const retreatQuery = {
  status: 'published',
  'filters[isActive][$eq]': true,
  'populate[coverImage]': 'true',
  'populate[gallery]': 'true',
};

const normalizeRetreat = retreat =>
  retreat && retreat.publishedAt && retreat.isActive === true
    ? {
        ...retreat,
        coverImage: normalizeMedia(retreat.coverImage),
        gallery: normalizeMediaCollection(retreat.gallery),
        websiteUrl: normalizeExternalUrl(retreat.websiteUrl),
        registrationUrl: normalizeExternalUrl(retreat.registrationUrl),
      }
    : null;

export const getRetreats = async () => {
  if (!STRAPI_BASE_URL) throw new Error('Retreat directory is not configured');
  const retreats = await fetchAllStrapiCollection('/retreats', {
    ...retreatQuery,
    sort: 'startDate:asc',
    'filters[endDate][$gte]': new Date().toISOString().slice(0, 10),
  });
  return retreats.map(normalizeRetreat).filter(Boolean);
};

export const getRetreatBySlug = async slug => {
  if (!slug) return null;
  if (!STRAPI_BASE_URL) throw new Error('Retreat directory is not configured');
  const response = await fetchStrapi('/retreats', {
    ...retreatQuery,
    'filters[slug][$eq]': slug,
    'pagination[limit]': 1,
  });
  return normalizeRetreat(normalizeCollection(response)[0]);
};

export const getCommunityCategoryLabel = (category, fallback = 'Community') => {
  if (typeof category === 'string') {
    return category.trim() || fallback;
  }

  return category?.label || category?.name || fallback;
};

export const getCommunityCategories = async () => {
  const categories = await fetchAllStrapiCollection('/community-categories', {
    sort: 'label:asc',
  });

  return categories.map(normalizeCommunityCategory).filter(Boolean);
};

export const getCommunityCategoryBySlug = async slug => {
  if (!slug) return null;

  const response = await fetchStrapi('/community-categories', {
    'populate[heroImage]': 'true',
    'filters[slug][$eq]': slug,
    'pagination[limit]': 1,
  });

  return normalizeCommunityCategory(normalizeCollection(response)[0]);
};

export const getEvents = async ({ categorySlug, featured, limit, upcoming } = {}) => {
  const normalizedCategorySlug = normalizeCommunityCategorySlug(categorySlug);
  const params = {
    'populate[state]': 'true',
    'populate[category]': 'true',
    'populate[coverImage]': 'true',
    'populate[gallery]': 'true',
    sort: 'startDateTime:asc',
  };
  if (limit) params['pagination[limit]'] = limit;
  if (featured) params['filters[featuredOnHomepage][$eq]'] = true;
  if (upcoming) params['filters[startDateTime][$gte]'] = new Date().toISOString();
  if (normalizedCategorySlug) {
    params['filters[category][slug][$eq]'] = normalizedCategorySlug;
  }

  const response = await fetchStrapi('/events', params);
  const events = normalizeCollection(response)
    .map(normalizeEvent)
    .filter(Boolean);

  return filterByCommunityCategorySlug(events, normalizedCategorySlug);
};

export const getEventBySlug = async slug => {
  if (!slug) return null;

  const response = await fetchStrapi('/events', {
    'populate[state]': 'true',
    'populate[category]': 'true',
    'populate[coverImage]': 'true',
    'populate[gallery]': 'true',
    'filters[slug][$eq]': slug,
    'pagination[limit]': 1,
  });
  const events = normalizeCollection(response);
  return normalizeEvent(events[0]);
};

export const getHomepageEvents = async ({ limit } = {}) => {
  const params = {
    'populate[state]': 'true',
    'populate[category]': 'true',
    'populate[coverImage]': 'true',
    'populate[gallery]': 'true',
    'filters[featuredOnHomepage][$eq]': true,
    'sort[0]': 'sortOrder:asc',
    'sort[1]': 'startDateTime:asc',
  };
  if (limit) params['pagination[limit]'] = limit;

  const response = await fetchStrapi('/events', params);
  const events = normalizeCollection(response);
  return events.map(normalizeEvent).filter(Boolean);
};

export const getHomepageInstructorStates = async ({ limit, instructorPreviewLimit } = {}) => {
  const params = {
    'populate[heroImage]': 'true',
    'populate[instructors]': 'true',
    'populate[instructors][populate][state]': 'true',
    'populate[instructors][populate][category]': 'true',
    'populate[instructors][populate][profileImage]': 'true',
    'populate[instructors][populate][coverImage]': 'true',
    'filters[featuredOnHomepage][$eq]': true,
    'sort[0]': 'sortOrder:asc',
    'sort[1]': 'createdAt:asc',
  };

  if (limit) params['pagination[limit]'] = limit;

  const response = await fetchStrapi('/instructor-states', params);
  const states = normalizeCollection(response)
    .map(normalizeInstructorState)
    .filter(Boolean);

  if (!instructorPreviewLimit) {
    return states;
  }

  return states.map(state => ({
    ...state,
    instructors: (state.instructors || []).slice(0, instructorPreviewLimit),
  }));
};

export const getInstructorStates = async ({ limit } = {}) => {
  const baseParams = {
    'populate[heroImage]': 'true',
  };

  const entities = limit
    ? normalizeCollection(
        await fetchStrapi('/instructor-states', {
          ...baseParams,
          'pagination[limit]': limit,
        })
      )
    : await fetchAllStrapiCollection('/instructor-states', baseParams);

  return entities
    .map(normalizeInstructorState)
    .filter(Boolean)
    .sort(sortByFeaturedAndOrder);
};

export const getInstructors = async ({ categorySlug, limit, stateSlug } = {}) => {
  const normalizedCategorySlug = normalizeCommunityCategorySlug(categorySlug);
  const baseParams = {
    'populate[state]': 'true',
    'populate[category]': 'true',
    'populate[profileImage]': 'true',
    'populate[coverImage]': 'true',
  };

  if (stateSlug) {
    baseParams['filters[state][slug][$eq]'] = stateSlug;
  }
  if (normalizedCategorySlug) {
    baseParams['filters[category][slug][$eq]'] = normalizedCategorySlug;
  }

  const entities = limit
    ? normalizeCollection(
        await fetchStrapi('/instructors', {
          ...baseParams,
          'pagination[limit]': limit,
        })
      )
    : await fetchAllStrapiCollection('/instructors', baseParams);

  const instructors = entities.map(normalizeInstructor).filter(Boolean);

  return filterByCommunityCategorySlug(instructors, normalizedCategorySlug).sort(
    sortByFeaturedAndOrder
  );
};

export const getInstructorBySlug = async slug => {
  if (!slug) return null;

  const response = await fetchStrapi('/instructors', {
    'populate[state]': 'true',
    'populate[category]': 'true',
    'populate[profileImage]': 'true',
    'populate[coverImage]': 'true',
    'filters[slug][$eq]': slug,
    'pagination[limit]': 1,
  });
  const instructors = normalizeCollection(response)
    .map(normalizeInstructor)
    .filter(Boolean);
  return instructors[0] || null;
};

export const getInstructorsByState = async stateSlug => {
  if (!stateSlug) return null;

  const response = await fetchStrapi('/instructor-states', {
    'filters[slug][$eq]': stateSlug,
    'pagination[limit]': 1,
    'populate[heroImage]': 'true',
    'populate[instructors][sort][0]': 'sortOrder:asc',
    'populate[instructors][sort][1]': 'name:asc',
    'populate[instructors][populate][state]': 'true',
    'populate[instructors][populate][category]': 'true',
    'populate[instructors][populate][profileImage]': 'true',
    'populate[instructors][populate][coverImage]': 'true',
  });
  const states = normalizeCollection(response)
    .map(normalizeInstructorState)
    .filter(Boolean);
  const state = states[0] || null;
  if (!state) return null;

  return {
    ...state,
    instructors: [...(state.instructors || [])].sort(sortByFeaturedAndOrder),
  };
};

export const getCommunityPosts = async ({ type, limit } = {}) => {
  try {
    const params = {
      sort: 'publishedAt:desc',
      populate: 'coverImage',
    };
    if (type) params['filters[type][$eq]'] = type;
    if (limit) params['pagination[limit]'] = limit;

    const response = await fetchStrapi('/community-posts', params);
    const posts = normalizeCollection(response);

    return posts.map(post => ({
      ...post,
      coverImage: normalizeMedia(post.coverImage),
    }));
  } catch (error) {
    return [];
  }
};

export const getCommunityVideos = async ({ featured, limit } = {}) => {
  try {
    const params = {
      sort: 'publishedAt:desc',
      populate: 'thumbnail,videoFile',
    };
    if (featured) params['filters[isFeatured][$eq]'] = true;
    if (limit) params['pagination[limit]'] = limit;

    const response = await fetchStrapi('/community-videos', params);
    const videos = normalizeCollection(response);

    return videos.map(video => ({
      ...video,
      thumbnail: normalizeMedia(video.thumbnail),
      videoFile: normalizeMedia(video.videoFile),
    }));
  } catch (error) {
    return [];
  }
};

export const getSocialLinks = async () => {
  try {
    const response = await fetchStrapi('/social-links', {
      sort: 'publishedAt:desc',
    });
    return normalizeCollection(response);
  } catch (error) {
    return [];
  }
};

export const getAffiliates = async () => {
  const response = await fetchAllStrapiCollection('/referrals', {
    'populate[logo]': 'true',
    'sort[0]': 'title:asc',
  });

  return response
    .map(referral => {
      const rawUrl = referral?.url || referral?.buttonLink;

      return {
        id: referral?.id,
        title: referral?.title || '',
        tagLine: referral?.tagLine || '',
        logoUrl: getStrapiMediaUrl(referral?.logo),
        buttonText: referral?.buttonText || '',
        url: normalizeExternalUrl(rawUrl),
      };
    })
    .sort((a, b) => normalizeText(a.title).localeCompare(normalizeText(b.title)));
};
